// OWMap BSL1 — App orchestrator
const { useState, useEffect, useRef, useCallback, useMemo } = React;

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "variation": "main",
  "culture": "all",
  "dayNight": 0.4,
  "motion": 1.0,
  "showH5": false,
  "showOnboarding": true
}/*EDITMODE-END*/;

// ── Live Beijing clock hook ──────────────────────────────────────────────────
function useLiveClock() {
  const [display, setDisplay] = useState({ time: '--:--', meta: '夜 · 北京 · 39.9° N', utc: '----.--.-- · --:--' });

  useEffect(() => {
    function update() {
      const now = new Date();
      // Beijing time = UTC+8
      const bj = new Date(now.getTime() + 8 * 3600 * 1000);
      const hh = String(bj.getUTCHours()).padStart(2, '0');
      const mm = String(bj.getUTCMinutes()).padStart(2, '0');
      const h = bj.getUTCHours();
      const period = h >= 5 && h < 12 ? '晨' : h >= 12 && h < 18 ? '昼' : h >= 18 && h < 22 ? '暮' : '夜';

      // UTC readout
      const uy = now.getUTCFullYear();
      const umo = String(now.getUTCMonth() + 1).padStart(2, '0');
      const ud = String(now.getUTCDate()).padStart(2, '0');
      const uh = String(now.getUTCHours()).padStart(2, '0');
      const um = String(now.getUTCMinutes()).padStart(2, '0');

      setDisplay({
        time: `${hh}:${mm}`,
        meta: `${period} · 北京 · 39.9° N`,
        utc: `${uy}.${umo}.${ud} · ${uh}:${um}`,
      });
    }

    update();
    const t = setInterval(update, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, []);

  return display;
}

// ── Error Boundary ───────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(err) { return { error: err }; }
  componentDidCatch(err, info) { console.error('[BSL1 ErrorBoundary]', err, info); }
  render() {
    if (this.state.error) {
      return React.createElement('div', { className: 'error-boundary' },
        React.createElement('div', { className: 'eb-icon' }, '⚠'),
        React.createElement('div', { className: 'eb-title' }, '模块加载异常'),
        React.createElement('div', { className: 'eb-msg' }, this.state.error.message),
        React.createElement('button', {
          className: 'eb-retry',
          onClick: () => this.setState({ error: null }),
        }, '重试'),
      );
    }
    return this.props.children;
  }
}

  // ── Service Worker registration ──────────────────────────────────────────────
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./sw.js')
      .then(reg => console.log('[BSL1] SW registered:', reg.scope))
      .catch(err => console.warn('[BSL1] SW registration failed:', err));
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────────
// parseDuration is also defined in right-panel.jsx; this local copy
// prevents a ReferenceError if app.jsx loads before right-panel.jsx.
function parseDuration(str) {
  if (!str) return 0;
  const parts = str.split(':').map(Number);
  return parts.length === 2 ? parts[0] * 60 + parts[1] : 0;
}

// ── SearchModal ──────────────────────────────────────────────────────────────
// Apple-style blurred center modal — search for locations and tracks.
function SearchModal({ onClose }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = query.trim().length < 1 ? [] :
    COORDS.filter(c =>
      (c.name || '').toLowerCase().includes(query.toLowerCase()) ||
      (c.region || '').toLowerCase().includes(query.toLowerCase())
    ).slice(0, 8);

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="搜索">
      <div className="search-modal" onClick={e => e.stopPropagation()}>
        <div className="search-modal__bar">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" className="search-modal__ico">
            <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.6"/>
            <path d="M15.5 15.5 L20 20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
          <input
            ref={inputRef}
            className="search-modal__input"
            type="text"
            placeholder="搜索地点、曲目、文化圈…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button className="search-modal__clear" onClick={() => setQuery('')} aria-label="清空">
              <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
        {filtered.length > 0 ? (
          <ul className="search-modal__results" role="listbox">
            {filtered.map(c => (
              <li key={c.id} className="search-modal__result" role="option" tabIndex={0}
                onClick={onClose}>
                <span className="search-modal__dot" style={{ background: CULTURES[c.culture]?.color }}></span>
                <div className="search-modal__result-text">
                  <span className="search-modal__result-name">{c.name}</span>
                  <span className="search-modal__result-meta">{c.region} · {CULTURES[c.culture]?.label}</span>
                </div>
                <span className="search-modal__result-coord">
                  {c.lat.toFixed(1)}°, {c.lng.toFixed(1)}°
                </span>
              </li>
            ))}
          </ul>
        ) : query.length > 0 ? (
          <div className="search-modal__empty">暂无匹配结果</div>
        ) : (
          <div className="search-modal__hint">
            <div className="search-hint-row">
              <span className="search-hint-label">热门</span>
              {['敦煌', '京都', '雷克雅未克', '廷巴克图'].map(n => (
                <button key={n} className="search-hint-chip"
                  onClick={() => setQuery(n)}>{n}</button>
              ))}
            </div>
          </div>
        )}
        <div className="search-modal__footer">
          <kbd className="search-kbd">ESC</kbd>
          <span>关闭</span>
          <span className="search-modal__sep">·</span>
          <kbd className="search-kbd">↵</kbd>
          <span>选择</span>
        </div>
      </div>
    </div>
  );
}

// ── MembershipModal ────────────────────────────────────────────────────────────
// Three-tier pricing modal to encourage membership conversion.
function MembershipModal({ onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const plans = [
    {
      id: 'free',
      tier: '免费开始',
      amount: '¥ 0',
      period: '/ 月 · 永久免费',
      features: [
        { text: '故事全部可见', on: true },
        { text: '每日 5 条故事音频', on: true },
        { text: '每日 5 次生成请求', on: true },
        { text: '最近 10 条播放历史', on: true },
        { text: '离线下载', on: false },
        { text: '高质量音频', on: false },
      ],
      cta: '当前方案',
      ctaStyle: 'plain',
    },
    {
      id: 'sub',
      tier: '订阅版',
      amount: '¥ 19.9',
      period: '/ 月 · 或 ¥ 168 / 年',
      badge: '推荐',
      features: [
        { text: '故事全部可见', on: true },
        { text: '无限制故事音频', on: true },
        { text: '每日 15 次生成请求', on: true },
        { text: '无限制播放历史', on: true },
        { text: '离线下载最多 50 条', on: true },
        { text: '高质量音频', on: true },
      ],
      cta: '免费试用 3 天',
      ctaStyle: 'gold',
      note: '随时取消，无需额外理由',
    },
    {
      id: 'pro',
      tier: '专业版',
      amount: '¥ 49',
      period: '/ 月',
      features: [
        { text: '全部订阅版功能', on: true },
        { text: '无限离线下载', on: true },
        { text: '每日 50 次生成请求', on: true },
        { text: '优先队列 · 专属客服', on: true },
        { text: 'API 访问权限', on: true },
        { text: '团队协作（最多 5 人）', on: true },
      ],
      cta: '联系我们',
      ctaStyle: 'outline',
    },
  ];

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="会员订阅">
      <div className="membership-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} aria-label="关闭">
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
        <div className="membership-modal__head">
          <div className="membership-modal__eyebrow">MEMBERSHIP · 订阅方案</div>
          <h2 className="membership-modal__title">开始你的故事旅行</h2>
          <p className="membership-modal__sub">免费开始，升级解锁全部体验。随时取消，无需理由。</p>
        </div>
        <div className="membership-modal__grid">
          {plans.map(p => (
            <div key={p.id} className={`mem-plan${p.badge ? ' mem-plan--featured' : ''}`}>
              {p.badge && <div className="mem-plan__badge">{p.badge}</div>}
              <div className="mem-plan__tier">{p.tier}</div>
              <div className="mem-plan__price">
                <span className="mem-plan__amount">{p.amount}</span>
                <span className="mem-plan__period">{p.period}</span>
              </div>
              <div className="mem-plan__divider"></div>
              <ul className="mem-plan__features">
                {p.features.map((f, i) => (
                  <li key={i} className={`mem-feature${f.on ? '' : ' mem-feature--off'}`}>
                    {f.on
                      ? <svg viewBox="0 0 16 16" width="12" height="12" fill="none"><path d="M3 8l4 4 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      : <svg viewBox="0 0 16 16" width="12" height="12" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                    }
                    <span>{f.text}</span>
                  </li>
                ))}
              </ul>
              <a
                className={`mem-plan__cta mem-plan__cta--${p.ctaStyle}`}
                href={p.id === 'free' ? undefined : 'https://owmap.com/auth/login.html'}
                onClick={p.id === 'free' ? onClose : undefined}
              >{p.cta}</a>
              {p.note && <p className="mem-plan__note">{p.note}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── SleepPlayerOverlay ────────────────────────────────────────────────────────
// Full-screen iframe overlay that hosts Sleep Player v2 and bridges state.
function SleepPlayerOverlay({ onClose }) {
  const iframeRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // Listen for return message from iframe
  useEffect(() => {
    function onMsg(e) {
      if (e.data && e.data.type === 'bsl1:sleep-exit') {
        if (typeof SleepBridge !== 'undefined' && e.data.payload) {
          const p = e.data.payload;
          SleepBridge.writeReturn(p.storyId, p.progress, p.playedMs);
        }
        onClose();
      }
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [onClose]);

  return (
    <div className="sleep-overlay" role="dialog" aria-modal="true" aria-label="助眠播放器">
      {/* iframe fades in after load */}
      <div className={`sleep-overlay__frame${loaded ? ' is-ready' : ''}`}>
        <iframe
          ref={iframeRef}
          src="./OWMap_SleepPlayer/Sleep Player v2.html"
          title="OWMap Sleep Player"
          className="sleep-overlay__iframe"
          onLoad={() => setLoaded(true)}
          allow="autoplay"
        />
      </div>
      {/* Loading indicator — outside frame so it's visible immediately */}
      {!loaded && (
        <div className="sleep-overlay__loading">
          <div className="sleep-loading-moon" />
          <p>进入夜晚…</p>
        </div>
      )}
      {/* Return button — always on top */}
      <button
        className="sleep-overlay__close"
        onClick={onClose}
        title="返回主界面"
        aria-label="关闭助眠播放器">
        <svg viewBox="0 0 22 22" width="14" height="14" fill="none">
          <path d="M5 11h12M5 11l4-4M5 11l4 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>返回</span>
      </button>
    </div>
  );
}

// ── App ──────────────────────────────────────────────────────────────────────
function App() {
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const [selectedCoord, setSelectedCoord] = useState(null);
  const [currentStory, setCurrentStory]   = useState(null);
  const [currentCoord, setCurrentCoord]   = useState(null);
  const [isPlaying, setIsPlaying]         = useState(false);
  const [progress, setProgress]           = useState(0);
  const [isBuffering, setIsBuffering]     = useState(false);
  const [showOnb, setShowOnb]             = useState(true);
  const [hoverCoord, setHoverCoord]       = useState(null);
  const [hoverPos, setHoverPos]           = useState({ x: 0, y: 0 });
  const [cursorLatLng, setCursorLatLng]   = useState(null);
  const [allCoords, setAllCoords]         = useState(COORDS);
  const [coordCount, setCoordCount]       = useState(COORDS.filter(c => !c.dormant).length);

  // Filter state
  const [cultureFilter, setCultureFilter] = useState([]);
  const [genreFilter, setGenreFilter]     = useState([]);
  const [styleSel, setStyleSel]           = useState("adult");
  const [duration, setDuration]           = useState([3, 20]);
  const [showH5, setShowH5]               = useState(false);
  const [showSleepPlayer, setShowSleepPlayer] = useState(false);
  const [uiHidden, setUiHidden]           = useState(false);
  const [showSearch, setShowSearch]       = useState(false);
  const [showMembership, setShowMembership] = useState(false);
  const [showTutorial, setShowTutorial]   = useState(false);

  // ── Brew module state ─────────────────────────────────────────────────────
  const [showBrew, setShowBrew]           = useState(false);
  const [brewInitialMode, setBrewInitMode] = useState(null);  // 'random'|'coordinate'|null
  const [brewInitCoord, setBrewInitCoord] = useState(null);   // pre-filled coord from globe long-press
  // userTier: resolved from auth session; defaults to 'F' in pure-frontend mode.
  // In production, app.jsx reads this from the session API or a global injected by SSR.
  const [userTier, setUserTier]           = useState(() => {
    // Attempt to read tier from a server-injected window global (set by BSL1 SSR/API)
    if (typeof window !== 'undefined' && window.__BSL1_USER_TIER__) {
      return window.__BSL1_USER_TIER__;
    }
    return 'F';
  });

  const globeRef = useRef(null);
  const clock = useLiveClock();

  // ── Init density coords on mount ─────────────────────────────────────────
  useEffect(() => {
    initCoords().then(merged => {
      setAllCoords(merged);
      setCoordCount(merged.filter(c => !c.dormant).length);
    }).catch(() => {
      // seed data already in state — just add density fill
      const expanded = initDensityCoords(COORDS);
      setAllCoords(expanded);
    });
  }, []);

  // ── Wire audio engine ─────────────────────────────────────────────────────
  useEffect(() => {
    BSL1Audio
      .on('play',      () => setIsPlaying(true))
      .on('pause',     () => setIsPlaying(false))
      .on('ended',     () => { setIsPlaying(false); setProgress(0); })
      .on('progress',  (p) => setProgress(p))
      .on('buffering', (v) => setIsBuffering(v))
      .on('error',     () => { setIsPlaying(false); setIsBuffering(false); });
    return () => BSL1Audio.destroy();
  }, []);

  // Sync onboarding with tweak
  useEffect(() => { if (tweaks.showOnboarding === false) setShowOnb(false); }, []);

  // Sync H5 with tweak
  useEffect(() => { setShowH5(!!tweaks.showH5); }, [tweaks.showH5]);

  // Wire cursor lat/lng from globe
  useEffect(() => {
    if (globeRef.current && globeRef.current.setCursorLatLngHandler) {
      globeRef.current.setCursorLatLngHandler(setCursorLatLng);
    }
  }, []);

  // Prefetch audio for current coord's stories via SW
  useEffect(() => {
    if (!selectedCoord || !('serviceWorker' in navigator)) return;
    const stories = getStories(selectedCoord.id);
    const ids = stories.map(s => s.id).slice(0, 3);
    // navigator.serviceWorker.ready may hang indefinitely on file:// or non-HTTPS
    try {
      navigator.serviceWorker.ready.then(reg => {
        reg.active?.postMessage({ type: 'PREFETCH_AUDIO', storyIds: ids });
      }).catch(() => {});
    } catch (_) {}
  }, [selectedCoord]);

  const handleSelectCoord = useCallback((coord) => {
    setSelectedCoord(coord);
    if (globeRef.current) globeRef.current.flyTo(coord.lat, coord.lng);
  }, []);

  // Navigate to prev/next active coord by index in allCoords
  const navigateCoord = useCallback((delta) => {
    const active = allCoords.filter(c => !c.dormant && c.name);
    if (active.length === 0) return;
    const cur = selectedCoord ? active.findIndex(c => c.id === selectedCoord.id) : -1;
    const next = ((cur + delta) + active.length) % active.length;
    handleSelectCoord(active[next]);
  }, [allCoords, selectedCoord, handleSelectCoord]);

  const handlePlay = useCallback(async (story, coord) => {
    const isSame = currentStory && currentStory.id === story.id;
    if (isSame) {
      BSL1Audio.toggle();
      return;
    }
    setCurrentStory(story);
    setCurrentCoord(coord);
    setProgress(0);
    if (!selectedCoord || selectedCoord.id !== coord.id) handleSelectCoord(coord);

    const src = BSL1Audio.resolveUrl(story.id);
    try {
      await BSL1Audio.load(story.id, src);
      BSL1Audio.play();
      BSL1API.logPlay(story.id, coord.id);
    } catch (_e) {
      // Audio file unavailable — use engine's fallback (handles orphan cleanup internally)
      const estDuration = parseDuration(story.duration) || 300;
      setIsPlaying(true);
      BSL1Audio.startFallbackPlayback(
        estDuration,
        (p) => setProgress(p),
        ()  => { setIsPlaying(false); setProgress(0); },
      );
    }
  }, [currentStory, selectedCoord, handleSelectCoord]);

  const handleToggle = useCallback(() => {
    if (currentStory) BSL1Audio.toggle();
  }, [currentStory]);

  const handleSeek = useCallback((ratio) => {
    BSL1Audio.seek(ratio);
    setProgress(ratio);
  }, []);

  const handleSmartPick = useCallback(() => {
    const candidates = allCoords.filter(c => !c.dormant && c.heat > 0.6);
    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    if (pick) handleSelectCoord(pick);
  }, [allCoords, handleSelectCoord]);

  // ── Sleep Player integration ──────────────────────────────────────────────
  const handleEnterSleep = useCallback(() => {
    if (typeof SleepBridge !== 'undefined') {
      const vol = (typeof BSL1Audio !== 'undefined' && BSL1Audio._audio)
        ? BSL1Audio._audio.volume : 0.7;
      SleepBridge.launch(currentStory, currentCoord, progress, vol, 'zh', 'embed');
    }
    setShowSleepPlayer(true);
  }, [currentStory, currentCoord, progress]);

  const handleExitSleep = useCallback(() => {
    setShowSleepPlayer(false);
    // Read return progress from bridge
    if (typeof SleepBridge !== 'undefined') {
      const ret = SleepBridge.readReturn();
      if (ret && ret.progress > 0 && currentStory && ret.story_id === currentStory.id) {
        const dur = (typeof BSL1Audio !== 'undefined') ? BSL1Audio.duration : 0;
        if (dur > 0) {
          BSL1Audio.seek(ret.progress);
          setProgress(ret.progress);
        }
      }
    }
  }, [currentStory]);

  // Listen for Sleep Player exit event
  useEffect(() => {
    function onExitSleep(e) { handleExitSleep(); }
    window.addEventListener('bsl1:exit-sleep', onExitSleep);
    return () => window.removeEventListener('bsl1:exit-sleep', onExitSleep);
  }, [handleExitSleep]);

  // Refresh userTier from server if BSL1 auth session is available
  useEffect(() => {
    if (typeof window !== 'undefined' && window.__BSL1_USER_TIER__) {
      setUserTier(window.__BSL1_USER_TIER__);
    }
    // Listen for post-login tier update (e.g. after OAuth redirect)
    function onTierUpdate(e) {
      if (e.detail?.tier) setUserTier(e.detail.tier);
    }
    window.addEventListener('bsl1:tier-update', onTierUpdate);
    return () => window.removeEventListener('bsl1:tier-update', onTierUpdate);
  }, []);

  // ── Globe long-press → Brew B coord picker ────────────────────────────────
  // Injects a long-press handler into the GlobeScene ref.
  // GlobeScene must expose globeRef.current._longPressCallback for this to work.
  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    g._longPressCallback = (coord) => {
      // Long-press on globe surface opens Brew B with pre-filled coord
      setBrewInitCoord(coord);
      setBrewInitMode('coordinate');
      setShowBrew(true);
    };
    return () => {
      if (globeRef.current) globeRef.current._longPressCallback = null;
    };
  }, []);

  // ── Brew handlers ─────────────────────────────────────────────────────────
  const handleOpenBrew = useCallback((opts = {}) => {
    setBrewInitMode(opts.mode || null);
    setBrewInitCoord(opts.coord || null);
    setShowBrew(true);
  }, []);

  const handleCloseBrew = useCallback(() => {
    setShowBrew(false);
    setBrewInitMode(null);
    setBrewInitCoord(null);
  }, []);

  // Play a brewed story: locate the coord and story in app state, or synthesise
  const handlePlayBrew = useCallback((brewResult) => {
    // Try to find the matching coord by place name or coordinates
    const targetCoord = allCoords.find(c =>
      (brewResult.resolved_place_name && c.name === brewResult.resolved_place_name) ||
      (brewResult.target_lat !== undefined &&
       Math.abs(c.lat - brewResult.target_lat) < 0.1 &&
       Math.abs(c.lng - brewResult.target_lon) < 0.1)
    ) || null;

    // Build a minimal story object from the brew result for the audio player
    const brewStory = {
      id:       brewResult.cnu_id || `brew-${brewResult.brew_id}`,
      title:    brewResult.story_title || '酿造故事',
      duration: brewResult.story_duration || '',
      culture:  brewResult.cultural_circle || null,
      isBrew:   true,
    };

    handlePlay(brewStory, targetCoord || { id: `brew-${brewResult.brew_id}`, name: brewResult.resolved_place_name || '未知地点', lat: brewResult.target_lat || 0, lng: brewResult.target_lon || 0 });
    handleCloseBrew();
  }, [allCoords, handlePlay, handleCloseBrew]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    function onKey(e) {
      // Don't fire when typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          if (currentStory) BSL1Audio.toggle();
          break;

        case 'ArrowLeft':
          e.preventDefault();
          // Navigate to previous coord in allCoords list
          navigateCoord(-1);
          break;

        case 'ArrowRight':
          e.preventDefault();
          navigateCoord(+1);
          break;

        case 'ArrowUp':
          e.preventDefault();
          BSL1Audio.setVolume(Math.min(1, (BSL1Audio._audio?.volume || 0.8) + 0.1));
          break;

        case 'ArrowDown':
          e.preventDefault();
          BSL1Audio.setVolume(Math.max(0, (BSL1Audio._audio?.volume || 0.8) - 0.1));
          break;

        case 'KeyM':
          BSL1Audio.setVolume(BSL1Audio._audio?.volume > 0 ? 0 : 0.8);
          break;

        case 'KeyR':
          // Random pick
          handleSmartPick();
          break;

        case 'KeyB':
          // Open Brew hub (random mode)
          if (typeof BrewFlow !== 'undefined') handleOpenBrew({ mode: 'random' });
          break;

        case 'Escape':
          if (showBrew) { handleCloseBrew(); return; }
          setSelectedCoord(null);
          break;

        default:
          break;
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentStory, allCoords, selectedCoord, handleSmartPick, showBrew, handleOpenBrew, handleCloseBrew]); // eslint-disable-line

  // Effective culture filter (from sidebar OR tweak override)
  const effectiveCultureFilter =
    tweaks.culture && tweaks.culture !== "all" ? [tweaks.culture] : cultureFilter;

  return (
    <ErrorBoundary>
      <div className={`app variation-${tweaks.variation}`} data-screen-label="01 Web Main" data-ui-hidden={uiHidden}>
        {/* Background ambient layers */}
        <div className="bg-cosmos"></div>
        <div className="bg-vignette"></div>
        <div className="bg-grain"></div>

        {/* Top HUD — clock (center-left) + action buttons (right) */}
        <div className="top-hud">
          <div className="hud-clock">
            <span className="hud-clock-time">{clock.time}</span>
            <span className="hud-clock-meta">{clock.meta}</span>
          </div>
          <div className="hud-actions">
            {/* 使用指南 */}
            <button className="hud-btn" title="使用指南" aria-label="使用指南"
              onClick={() => setShowTutorial(true)}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M12 11.5v4M12 8.5h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </button>
            {/* 搜索 */}
            <button className="hud-btn" title="搜索地点与曲目" aria-label="搜索"
              onClick={() => setShowSearch(true)}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M15.5 15.5 L20 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            {/* 会员 */}
            <button className="hud-btn hud-btn-member" title="会员与订阅" aria-label="会员"
              onClick={() => setShowMembership(true)}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                  stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
            </button>
            {/* Brew 故事酿造 */}
            {typeof BrewFlow !== 'undefined' && (
              <button
                className={`hud-btn hud-btn-brew${showBrew ? ' is-active' : ''}`}
                title="Brew · 故事酿造工坊 [B]"
                aria-label="故事酿造"
                onClick={() => showBrew ? handleCloseBrew() : handleOpenBrew({ mode: 'random' })}>
                {/* Flask icon inline — same as BrewIcon but without React namespace dep */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 3 L9 9 L5 17 C4.4 18.1 5.2 19.5 6.5 19.5 L17.5 19.5 C18.8 19.5 19.6 18.1 19 17 L15 9 L15 3"
                    stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7.5 3 L16.5 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  <path d="M7 14 Q9 12.5 12 13.5 Q15 14.5 17 13"
                    stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" opacity="0.6"/>
                </svg>
              </button>
            )}
            {/* H5 预览 */}
            <button className="hud-btn" title="H5 手机预览" aria-label="H5预览"
              onClick={() => setShowH5(!showH5)}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                <rect x="8" y="3" width="8" height="18" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                <circle cx="12" cy="18" r="0.7" fill="currentColor"/>
              </svg>
            </button>
            {/* 登录 */}
            <a className="hud-btn hud-btn-primary" href="https://owmap.com/auth/login.html"
              title="登录 OWMap" aria-label="登录">登录</a>
          </div>
        </div>

        {/* Three-column layout */}
        <main className="main-grid">
          <ErrorBoundary>
            <LeftPanel
              tweaks={tweaks} setTweak={setTweak}
              onSmartPick={handleSmartPick}
              cultureFilter={cultureFilter} setCultureFilter={setCultureFilter}
              genreFilter={genreFilter} setGenreFilter={setGenreFilter}
              styleSel={styleSel} setStyleSel={setStyleSel}
              duration={duration} setDuration={setDuration}
            />
          </ErrorBoundary>

          <section className="globe-area">
            <ErrorBoundary>
              <GlobeScene
                ref={globeRef}
                coords={allCoords}
                selectedId={selectedCoord ? selectedCoord.id : null}
                playingId={isPlaying && currentCoord ? currentCoord.id : null}
                onSelectCoord={handleSelectCoord}
                onHoverCoord={(c, x, y) => {
                  setHoverCoord(c);
                  if (c) setHoverPos({ x, y });
                }}
                dayNight={tweaks.dayNight}
                motion={tweaks.motion}
                variation={tweaks.variation}
                cultureFilter={effectiveCultureFilter}
              />
            </ErrorBoundary>

            {/* Reticle ring around globe */}
            <div className="globe-reticle" aria-hidden="true">
              <svg viewBox="0 0 800 800" className="globe-reticle-svg">
                <defs>
                  <radialGradient id="reticleFade" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgba(245,200,66,0)" />
                    <stop offset="92%" stopColor="rgba(245,200,66,0)" />
                    <stop offset="96%" stopColor="rgba(245,200,66,0.18)" />
                    <stop offset="100%" stopColor="rgba(245,200,66,0)" />
                  </radialGradient>
                </defs>
                <circle cx="400" cy="400" r="320" fill="none" stroke="rgba(232,232,240,0.06)" strokeWidth="1" strokeDasharray="2 6"/>
                <circle cx="400" cy="400" r="360" fill="none" stroke="rgba(232,232,240,0.04)" strokeWidth="1"/>
                {[0, 45, 90, 135, 180, 225, 270, 315].map(deg => {
                  const r1 = 360, r2 = deg % 90 === 0 ? 376 : 370;
                  const rad = (deg - 90) * Math.PI / 180;
                  const x1 = 400 + Math.cos(rad) * r1, y1 = 400 + Math.sin(rad) * r1;
                  const x2 = 400 + Math.cos(rad) * r2, y2 = 400 + Math.sin(rad) * r2;
                  return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(245,200,66,0.4)" strokeWidth={deg % 90 === 0 ? 1.5 : 0.8}/>;
                })}
                {[{d:0,t:"N"},{d:90,t:"E"},{d:180,t:"S"},{d:270,t:"W"}].map(({d,t}) => {
                  const rad = (d - 90) * Math.PI / 180;
                  const x = 400 + Math.cos(rad) * 392, y = 400 + Math.sin(rad) * 392 + 4;
                  return <text key={t} x={x} y={y} fill="rgba(245,200,66,0.55)" fontSize="11" textAnchor="middle" fontFamily="monospace" letterSpacing="2">{t}</text>;
                })}
              </svg>
            </div>

            {/* Top-left: viewport status */}
            <div className="globe-hud-tl">
              <div className="globe-stat-pill">
                <span className="globe-stat-dot"></span>
                <span>{coordCount} 个活跃坐标 · 6 文化圈</span>
              </div>
              <div className="globe-stat-meta">
                <span className="meta-key">PROJECTION</span>
                <span className="meta-val">ORTHOGRAPHIC&nbsp;·&nbsp;1.00×</span>
              </div>
            </div>

            {/* Bottom-left: legend */}
            <div className="globe-hud-bl">
              <div className="globe-legend">
                <div className="legend-head">CULTURAL FIELDS</div>
                {Object.values(CULTURES).map(c => (
                  <div key={c.id} className="legend-row">
                    <span className="legend-dot" style={{ background: c.color }}></span>
                    <span className="legend-label">{c.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom-right: live geo readout */}
            <div className="globe-hud-br">
              <div className="globe-coord-readout">
                <div className="readout-line">
                  <span className="readout-key">LAT</span>
                  <span className="readout-val">{cursorLatLng ? `${cursorLatLng.lat.toFixed(2)}°${cursorLatLng.lat >= 0 ? "N" : "S"}` : "—"}</span>
                </div>
                <div className="readout-line">
                  <span className="readout-key">LNG</span>
                  <span className="readout-val">{cursorLatLng ? `${Math.abs(((cursorLatLng.lng + 540) % 360) - 180).toFixed(2)}°${((cursorLatLng.lng + 540) % 360) - 180 >= 0 ? "E" : "W"}` : "—"}</span>
                </div>
                <div className="readout-divider"></div>
                <div className="readout-line">
                  <span className="readout-key">UTC</span>
                  <span className="readout-val">{clock.utc}</span>
                </div>
                <div className="readout-line">
                  <span className="readout-key">VIEW</span>
                  <span className="readout-val">{selectedCoord ? selectedCoord.name : "GLOBAL"}</span>
                </div>
              </div>
            </div>

            {/* Bottom-right: Sleep + Zen action cluster */}
            <div className="globe-action-cluster">
              <button
                className={`sleep-launch-btn${isPlaying ? ' has-story' : ''}`}
                title="进入助眠播放器"
                onClick={handleEnterSleep}
                aria-label="进入助眠播放器">
                <span className="sleep-btn-moon">
                  <svg viewBox="0 0 22 22" width="18" height="18" fill="none">
                    <path d="M17.5 13.5A8 8 0 0 1 9 5a8.3 8.3 0 0 0-.5 0A8 8 0 1 0 18 14.3a7.5 7.5 0 0 1-.5-.8Z"
                      fill="currentColor" fillOpacity="0.9"/>
                  </svg>
                </span>
                <span className="sleep-btn-label">
                  <span className="sleep-btn-cn">助眠</span>
                  <span className="sleep-btn-en">SLEEP</span>
                </span>
                {isPlaying && <span className="sleep-btn-live" />}
              </button>

              <button
                className={`zen-toggle-btn${uiHidden ? ' is-active' : ''}`}
                title={uiHidden ? '恢复界面' : '简化界面'}
                onClick={() => setUiHidden(v => !v)}
                aria-label={uiHidden ? '恢复界面' : '简化界面'}>
                <svg viewBox="0 0 22 22" width="14" height="14" fill="none">
                  {uiHidden ? (
                    <><circle cx="11" cy="11" r="3" stroke="currentColor" strokeWidth="1.2"/>
                    <path d="M11 4v2M11 16v2M4 11h2M16 11h2M6.3 6.3l1.4 1.4M14.3 14.3l1.4 1.4M14.3 6.3l-1.4 1.4M6.3 14.3l1.4 1.4" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></>
                  ) : (
                    <><path d="M4 7h14M4 11h10M4 15h7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></>
                  )}
                </svg>
                <span className="zen-btn-label">{uiHidden ? '还原' : '简化'}</span>
              </button>
            </div>

            {/* Top-right: equator/tropics legend */}
            <div className="globe-hud-tr">
              <div className="geo-key">
                <div className="geo-key-row"><span className="geo-key-line geo-key-eq"></span><span>EQUATOR · 0°</span></div>
                <div className="geo-key-row"><span className="geo-key-line geo-key-tr"></span><span>TROPICS · ±23.5°</span></div>
                <div className="geo-key-row"><span className="geo-key-line geo-key-pc"></span><span>POLAR · ±66.5°</span></div>
              </div>
            </div>

            {/* Hover tooltip */}
            {hoverCoord && (
              <div className="globe-tooltip" style={{ left: hoverPos.x + 14, top: hoverPos.y + 14 }}>
                <div className="tt-culture" style={{ color: CULTURES[hoverCoord.culture].color }}>{CULTURES[hoverCoord.culture].label}</div>
                <div className="tt-title">{hoverCoord.name || '—'}</div>
                <div className="tt-coord">{hoverCoord.lat.toFixed(2)}° · {hoverCoord.lng.toFixed(2)}°</div>
                {hoverCoord.count > 0 && <div className="tt-count">{hoverCoord.count} 个故事</div>}
                {!hoverCoord.dormant && <div className="tt-hint">点击进入坐标</div>}
              </div>
            )}

            {/* Buffering indicator */}
            {isBuffering && (
              <div className="globe-buffering" aria-label="加载音频中">
                <div className="buffering-spinner"></div>
              </div>
            )}

            {/* Variation B caption */}
            {tweaks.variation === "avant" && (
              <div className="globe-hud-top">
                <div className="avant-caption">
                  <span className="avant-caption-num">VAR · B</span>
                  <span className="avant-caption-text">A LIVING SCORE / by REFIK-ADJACENT FIELD GENERATIVE</span>
                </div>
              </div>
            )}
          </section>

          <ErrorBoundary>
            <RightPanel
              selectedCoord={selectedCoord}
              currentStory={currentStory}
              isPlaying={isPlaying}
              onPlay={handlePlay}
            />
          </ErrorBoundary>
        </main>

        <PlayBar
          currentStory={currentStory}
          currentCoord={currentCoord}
          isPlaying={isPlaying}
          isBuffering={isBuffering}
          onToggle={handleToggle}
          onSeek={handleSeek}
          progress={progress}
        />

        {showOnb && (
          <Onboarding
            onDone={() => setShowOnb(false)}
            onOpenTutorial={() => { setShowOnb(false); setShowTutorial(true); }}
          />
        )}

        {showTutorial && typeof Tutorial !== 'undefined' && (
          <Tutorial onClose={() => setShowTutorial(false)} />
        )}

        {showH5 && <H5Shell onClose={() => setShowH5(false)} />}

        {/* Sleep Player overlay */}
        {showSleepPlayer && (
          <SleepPlayerOverlay
            currentStory={currentStory}
            currentCoord={currentCoord}
            progress={progress}
            onClose={handleExitSleep}
          />
        )}

        {/* Brew module — story brewing flow */}
        {showBrew && typeof BrewFlow !== 'undefined' && (
          <ErrorBoundary>
            <BrewFlow
              initialMode={brewInitialMode}
              coordInfo={brewInitCoord}
              globeRef={globeRef}
              userTier={userTier}
              onClose={handleCloseBrew}
              onPlayBrew={handlePlayBrew}
            />
          </ErrorBoundary>
        )}

        {/* Search modal */}
        {showSearch && <SearchModal onClose={() => setShowSearch(false)} />}

        {/* Membership modal */}
        {showMembership && <MembershipModal onClose={() => setShowMembership(false)} />}

        {/* Tweaks */}
        <TweaksPanel title="Tweaks">
          <TweakSection label="设计方向 · Variation"/>
          <TweakRadio label="方向" value={tweaks.variation}
            options={[{value:"main", label:"静谧"}, {value:"avant", label:"前卫"}]}
            onChange={(v) => setTweak("variation", v)}/>

          <TweakSection label="文化圈滤镜"/>
          <TweakSelect label="文化圈" value={tweaks.culture}
            options={[
              {value:"all", label:"全部 · 六个文化圈"},
              {value:"east_asia", label:"东亚"},
              {value:"europe", label:"欧洲"},
              {value:"middle_east", label:"中东 · 北非"},
              {value:"south_asia", label:"南亚 · 东南亚"},
              {value:"americas", label:"美洲"},
              {value:"africa", label:"非洲"},
            ]}
            onChange={(v) => setTweak("culture", v)}/>

          <TweakSection label="昼夜与节奏"/>
          <TweakSlider label="日光强度" value={tweaks.dayNight} min={0} max={1} step={0.05}
            onChange={(v) => setTweak("dayNight", v)}/>
          <TweakSlider label="动效节奏" value={tweaks.motion} min={0.2} max={2.0} step={0.1} unit="×"
            onChange={(v) => setTweak("motion", v)}/>

          <TweakSection label="视图"/>
          <TweakToggle label="H5 移动端预览" value={tweaks.showH5}
            onChange={(v) => setTweak("showH5", v)}/>
          <TweakButton label="重新进入引导" onClick={() => setShowOnb(true)}/>
        </TweaksPanel>
      </div>
    </ErrorBoundary>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
